import React from "react";
import {
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhone,
  FaShoppingBag,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";

interface SocialLink {
  platform: string;
  url: string;
  icon: string;
  color?: string;
}

interface ContactInfo {
  phone: string;
  email: string;
  address: string;
  social: SocialLink[];
}

interface FooterInfo {
  title: string;
  description: string;
  copyright: string;
}

interface FooterLink {
  label: string;
  target: string;
}

interface FooterProps {
  footer: FooterInfo;
  contact: ContactInfo;
  links?: FooterLink[];
  logo?: React.ReactNode;
  sectionId?: string;
  onNavigate?: (target: string) => void;
}

const socialIcons: Record<string, React.ReactNode> = {
  FaFacebook: <FaFacebook />,
  FaInstagram: <FaInstagram />,
  FaTwitter: <FaTwitter />,
  FaYoutube: <FaYoutube />,
};

export default function Footer({
  footer,
  contact,
  links = [],
  logo = <FaShoppingBag className="text-3xl" />,
  sectionId = "contact",
  onNavigate,
}: FooterProps) {
  return (
    <section id={sectionId} className="bg-gray-900 text-white py-16">
      <div className="container mx-auto max-w-7xl px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              {logo}
              <h3 className="font-primary text-2xl font-bold">
                {footer.title}
              </h3>
            </div>

            <p className="font-secondary text-gray-400 leading-relaxed">
              {footer.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-primary font-bold text-lg mb-4">
              Quick Links
            </h4>

            <ul className="space-y-2">
              {links.map((link) => (
                <li key={link.target}>
                  <button
                    onClick={() => onNavigate?.(link.target)}
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-primary font-bold text-lg mb-4">
              Contact
            </h4>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-400">
                <FaPhone className="text-blue-400" />
                <span>{contact.phone}</span>
              </div>

              <div className="flex items-center gap-3 text-gray-400">
                <FaEnvelope className="text-blue-400" />
                <span>{contact.email}</span>
              </div>

              <div className="flex items-center gap-3 text-gray-400">
                <FaMapMarkerAlt className="text-blue-400" />
                <span>{contact.address}</span>
              </div>
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-primary font-bold text-lg mb-4">
              Follow Us
            </h4>

            <div className="flex gap-4">
              {contact.social.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-12 h-12 rounded-full ${
                    social.color || "bg-blue-600"
                  } hover:opacity-80 transition-all duration-200 flex items-center justify-center text-xl`}
                  aria-label={social.platform}
                >
                  {socialIcons[social.icon]}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 font-secondary text-sm">
          <p>{footer.copyright}</p>
        </div>
      </div>
    </section>
  );
}